import React, { useState } from 'react';
import { WisdomScrollIcon, ConfirmCheckIcon, SaveThreadIcon } from './CustomIcons';
import GrantMathLavaLamp from './GrantMathLavaLamp';

/**
 * Epic Summary Generator
 * Creates spellbinding summary of conversation using all M.ai.ry wisdom
 */
const EpicSummary = ({ conversation, onClose, onSaveToDashboard, variant = 'terminal' }) => {
  const [summary, setSummary] = useState(null);
  const [generating, setGenerating] = useState(true);
  const [saved, setSaved] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || '';

  React.useEffect(() => {
    generateEpicSummary();
  }, []);

  const generateEpicSummary = async () => {
    try {
      const response = await fetch(`${API}/api/conversations/epic-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: conversation.history || conversation.messages || [],
          interface_mode: variant,
          orbParams: conversation.orbParams
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else {
        setSummary({ error: 'Failed to generate summary' });
      }
    } catch (error) {
      console.error('Summary generation error:', error);
      setSummary({ error: 'Connection error' });
    }
    setGenerating(false);
  };

  const handleSave = async () => {
    if (onSaveToDashboard && summary) {
      await onSaveToDashboard(summary);
      setSaved(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  };

  const handleDownloadPDF = () => {
    // Trigger PDF download
    window.open(`${API}/api/conversations/download-summary-pdf?session=${conversation.id}`, '_blank');
  };

  const colors = variant === 'terminal' 
    ? { primary: '#fbbf24', secondary: '#f59e0b', bg: 'rgba(0,0,0,0.95)' }
    : { primary: '#fbbf24', secondary: '#f59e0b', bg: 'rgba(255,255,255,0.95)' };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Dim fractal background */}
      <div className="absolute inset-0 opacity-10">
        <GrantMathLavaLamp params={conversation.orbParams || {}} />
      </div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />

      {/* Summary Container */}
      <div 
        className="relative z-10 max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-2xl border-2 shadow-2xl"
        style={{
          background: colors.bg,
          borderColor: colors.primary + '80',
          boxShadow: `0 0 60px ${colors.primary}40, 0 20px 40px rgba(0,0,0,0.5)`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="border-b p-6"
          style={{
            borderColor: colors.primary + '30',
            background: `linear-gradient(135deg, ${colors.primary}15, ${colors.secondary}10)`
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <WisdomScrollIcon size={32} color={colors.primary} className="animate-pulse" />
              <h2 
                className="text-3xl font-bold"
                style={{ 
                  color: colors.primary,
                  fontFamily: '"Cinzel", serif',
                  textShadow: `0 0 20px ${colors.primary}40`
                }}
              >
                Your Wisdom Summary
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-2xl opacity-50 hover:opacity-100 transition-opacity"
              style={{ color: colors.primary }}
            >
              ×
            </button>
          </div>
          <p 
            className="text-sm opacity-70"
            style={{ 
              color: colors.primary,
              fontFamily: '"Crimson Text", serif' 
            }}
          >
            A gift of insight from your conversation with M.ai.ry
          </p>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh] p-6">
          {generating ? (
            <div className="text-center py-16">
              <div className="mb-6">
                <WisdomScrollIcon size={64} color={colors.primary} className="mx-auto animate-pulse" />
              </div>
              <p 
                className="text-lg mb-2 animate-pulse"
                style={{ 
                  color: colors.primary,
                  fontFamily: '"Cormorant Garamond", serif' 
                }}
              >
                Weaving your wisdom...
              </p>
              <p className="text-sm opacity-60" style={{ color: colors.primary }}>
                M.ai.ry is creating something beautiful for you
              </p>
            </div>
          ) : summary?.error ? (
            <div className="text-center py-16">
              <p style={{ color: '#ef4444' }}>{summary.error}</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Essence */}
              {summary?.essence && (
                <section>
                  <h3 
                    className="text-xl mb-3 flex items-center gap-2"
                    style={{ 
                      color: colors.primary,
                      fontFamily: '"Cinzel", serif' 
                    }}
                  >
                    <span className="text-2xl">✨</span>
                    The Essence
                  </h3>
                  <p 
                    className="text-lg leading-relaxed"
                    style={{ 
                      color: colors.primary,
                      opacity: 0.9,
                      fontFamily: '"Crimson Text", serif',
                      fontStyle: 'italic'
                    }}
                  >
                    {summary.essence}
                  </p>
                </section>
              )}

              {/* Key Insights */}
              {summary?.insights && summary.insights.length > 0 && (
                <section>
                  <h3 
                    className="text-xl mb-4"
                    style={{ 
                      color: colors.primary,
                      fontFamily: '"Cinzel", serif' 
                    }}
                  >
                    Key Insights
                  </h3>
                  <div className="space-y-3">
                    {summary.insights.map((insight, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-3 p-4 rounded-lg border"
                        style={{
                          borderColor: colors.primary + '30',
                          background: colors.primary + '05'
                        }}
                      >
                        <span style={{ color: colors.primary }}>•</span>
                        <p 
                          style={{ 
                            color: colors.primary,
                            opacity: 0.85,
                            fontFamily: '"Crimson Text", serif' 
                          }}
                        >
                          {insight}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Questions Explored */}
              {summary?.questions && summary.questions.length > 0 && (
                <section>
                  <h3 
                    className="text-xl mb-3"
                    style={{ 
                      color: colors.primary,
                      fontFamily: '"Cinzel", serif' 
                    }}
                  >
                    Questions You Explored
                  </h3>
                  <ul className="space-y-2">
                    {summary.questions.map((q, idx) => (
                      <li 
                        key={idx}
                        className="text-sm opacity-70"
                        style={{ 
                          color: colors.primary,
                          fontFamily: '"Crimson Text", serif' 
                        }}
                      >
                        → {q}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Actionable Wisdom */}
              {summary?.actionable && (
                <section>
                  <h3 
                    className="text-xl mb-3"
                    style={{ 
                      color: colors.primary,
                      fontFamily: '"Cinzel", serif' 
                    }}
                  >
                    Take With You
                  </h3>
                  <p 
                    className="leading-relaxed"
                    style={{ 
                      color: colors.primary,
                      opacity: 0.85,
                      fontFamily: '"Crimson Text", serif' 
                    }}
                  >
                    {summary.actionable}
                  </p>
                </section>
              )}

              {/* Related Papers */}
              {summary?.related_papers && summary.related_papers.length > 0 && (
                <section>
                  <h3 
                    className="text-xl mb-3"
                    style={{ 
                      color: colors.primary,
                      fontFamily: '"Cinzel", serif' 
                    }}
                  >
                    Related Wisdom
                  </h3>
                  <div className="space-y-2">
                    {summary.related_papers.map((paper, idx) => (
                      <a
                        key={idx}
                        href={`/?paper=${paper.id}`}
                        className="block p-3 rounded-lg border hover:border-opacity-60 transition-all"
                        style={{
                          borderColor: colors.primary + '30',
                          background: colors.primary + '05'
                        }}
                      >
                        <p 
                          className="font-medium mb-1"
                          style={{ 
                            color: colors.primary,
                            fontFamily: '"Crimson Text", serif' 
                          }}
                        >
                          {paper.title}
                        </p>
                        <p className="text-xs opacity-60" style={{ color: colors.primary }}>
                          {paper.date}
                        </p>
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* Constitutional Alignment */}
              {summary?.constitution_alignment && (
                <section className="border-t pt-6" style={{ borderColor: colors.primary + '20' }}>
                  <p 
                    className="text-sm italic opacity-70"
                    style={{ 
                      color: colors.primary,
                      fontFamily: '"Cormorant Garamond", serif' 
                    }}
                  >
                    This conversation aligned with: {summary.constitution_alignment.join(', ')}
                  </p>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {!generating && !summary?.error && (
          <div 
            className="border-t p-6 flex items-center justify-between"
            style={{ borderColor: colors.primary + '30' }}
          >
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saved}
                className="px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2"
                style={{
                  background: saved 
                    ? `${colors.primary}20` 
                    : `linear-gradient(135deg, ${colors.primary}40, ${colors.secondary}40)`,
                  border: `1px solid ${colors.primary}60`,
                  color: variant === 'terminal' ? '#000' : '#fff',
                  fontFamily: '"Cinzel", serif'
                }}
              >
                {saved ? (
                  <><ConfirmCheckIcon size={16} /> Saved to Dashboard</>
                ) : (
                  <><SaveThreadIcon size={16} /> Save to Dashboard</>
                )}
              </button>

              <button
                onClick={handleDownloadPDF}
                className="px-6 py-3 rounded-lg font-medium border transition-all"
                style={{
                  borderColor: colors.primary + '40',
                  color: colors.primary,
                  fontFamily: '"Cinzel", serif'
                }}
              >
                📄 Download PDF
              </button>
            </div>

            <button
              onClick={() => navigator.clipboard.writeText(JSON.stringify(summary, null, 2))}
              className="text-sm opacity-60 hover:opacity-100 transition-opacity"
              style={{ color: colors.primary }}
            >
              📋 Copy Summary
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EpicSummary;
